// utils/toAbsoluteUrl.ts
/**
 * Convert a (possibly) relative URL to an absolute URL.
 * Works both in browser and in server (Nuxt/Nitro) context when you provide a base.
 *
 * If `options.base` is omitted and the function runs in the browser, window.location.origin is used.
 * If running in SSR, pass a `base` or use baseFromEvent(useRequestEvent()) to get the origin.
 *
 * "undecorated" option:
 *  - strips query string and hash
 *  - removes trailing slash (except for root "/")
 */

export type ToAbsoluteOptions = {
  /** Base origin (eg. "https://example.com" or "http://localhost:3000"). If omitted, browser origin is used. */
  base?: string;
  /** When true remove query string and hash and normalize trailing slash. Default false. */
  undecorated?: boolean;
  /**
   * If true, throw an error when `base` can't be detected for a relative url.
   * Default false -> returns the original input string unchanged when base missing.
   */
  throwOnMissingBase?: boolean;
};

/**
 * Try to build an origin string from a Nitro request event.
 * Usage in Nuxt SSR: const base = baseFromEvent(useRequestEvent());
 *
 * Accepts either the full event object or an object that has request headers in:
 *  - event.node.req.headers (Nitro)
 *  - event.req.headers
 *  - event.headers
 *
 * Returns undefined when it cannot determine origin.
 */
export function baseFromEvent(event: any): string | undefined {
  if (!event) return undefined;

  // Common places for headers on Nitro / Nuxt events
  const headers =
    event.node?.req?.headers ??
    event.req?.headers ??
    event.headers ??
    (event.request && event.request.headers) ??
    undefined;

  if (!headers) return undefined;

  const host = headers.host || headers.Host;
  if (!host) return undefined;

  // Try common headers for proto, default to http
  const proto =
    headers["x-forwarded-proto"] ||
    headers["x-forwarded-protocol"] ||
    headers[":scheme"] ||
    headers["x-forwarded-protocol"] ||
    headers["x-url-scheme"] ||
    "http";

  // Some proxies set comma-separated values; take first
  const protoClean = String(proto).split(",")[0].trim();

  return `${protoClean}://${host}`;
}

/**
 * Convert single url string to absolute.
 */
export function toAbsoluteUrl(input: string, options: ToAbsoluteOptions = {}): string {
  if (!input || typeof input !== "string") return input;

  // Fast check: if input already looks absolute (has scheme), return normalized / undecorated if requested.
  const isAbsoluteLike = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(input) || input.startsWith("//");

  // Decide base
  let base = options.base;

  if (!base && typeof window !== "undefined" && typeof window.location !== "undefined") {
    base = window.location.origin;
  }

  // If input is absolute-like and no base -- let URL parse it directly
  try {
    const url = isAbsoluteLike ? new URL(input, base || undefined) : new URL(input, base || undefined);

    if (!base && !isAbsoluteLike) {
      // relative + no base -> nothing we can do reliably:
      if (options.throwOnMissingBase) {
        throw new Error(
          "toAbsoluteUrl: cannot resolve relative url without base (server context). Pass options.base or call baseFromEvent(useRequestEvent())."
        );
      }
      // return input unchanged for best-effort behavior
      return input;
    }

    if (options.undecorated) {
      url.search = "";
      url.hash = "";
      // remove trailing slash except when pathname === '/'
      if (url.pathname.length > 1 && url.pathname.endsWith("/")) {
        url.pathname = url.pathname.replace(/\/+$/, "");
      }
    }

    // toString will include default port if present (e.g. :3000). That's expected/desired.
    return url.toString();
  } catch (err) {
    // In case URL constructor fails, gracefully return the original input or throw depending on option
    if (options.throwOnMissingBase) throw err;
    return input;
  }
}

/**
 * Convert an array (or map) of urls returning an array of absolute urls (preserves falsy values).
 */
export function useAbsoluteUrls(urls: Array<string | null | undefined>, options: ToAbsoluteOptions = {}) {
  return urls.map((u) => (u ? toAbsoluteUrl(u, options) : u));
}

export function useAbsoluteUrl(url: string, options: ToAbsoluteOptions = {}) {
  return useAbsoluteUrls([url], options)[0];
}
